import { makePersisted } from "@solid-primitives/storage";
import { createSignal } from "solid-js";
import { createStore, produce, reconcile } from "solid-js/store";
import { isServer } from "solid-js/web";
import { isJobCategory } from "../../create/constants";
import type { AiJobOfferPayload, ChatMessage, ChatMessageFile } from "../types";

type AiChatDraft = {
  messages: ChatMessage[];
  createdAt: number;
  partialPayload: Partial<AiJobOfferPayload> | null;
};

const TTL_MS = 30 * 60 * 1000;
const STORAGE_KEY = "ai_chat_draft";

type CreateAiChatStoreOptions = {
  storage?: Storage;
  /** @internal Override storage key for test isolation */
  storageKey?: string;
};

function checkAndClearExpiredDraft(storage: Storage | undefined, key: string) {
  if (!storage) return;
  const raw = storage.getItem(key);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Partial<AiChatDraft>;
    if (
      parsed.createdAt &&
      parsed.createdAt > 0 &&
      parsed.createdAt + TTL_MS < Date.now()
    ) {
      storage.removeItem(key);
    }
  } catch {
    storage.removeItem(key);
  }
}

/**
 * Reads a persisted draft. A draft saved before the taxonomy release may carry
 * one of the 4 removed categories: the key is dropped (absent, not `null`) so
 * the refine reopens with an empty category field. Messages and the rest of the
 * payload stay as they were.
 */
function deserializeAiChatDraft(raw: string): AiChatDraft {
  const draft = JSON.parse(raw) as AiChatDraft;
  const payload = draft?.partialPayload;
  if (payload?.job_category && !isJobCategory(payload.job_category)) {
    delete payload.job_category;
  }
  return draft;
}

function createAiChatStore(options?: CreateAiChatStoreOptions) {
  const storage = options?.storage ?? (isServer ? undefined : localStorage);
  const key = options?.storageKey ?? STORAGE_KEY;

  // TTL check before makePersisted restores state
  checkAndClearExpiredDraft(storage, key);

  const [state, setState] = makePersisted(
    createStore<AiChatDraft>({
      messages: [],
      createdAt: 0,
      partialPayload: null,
    }),
    {
      name: key,
      storage: storage as Storage | undefined,
      deserialize: deserializeAiChatDraft,
    },
  );

  // Separate signal for in-progress assistant text (not persisted)
  const [streamingText, setStreamingText] = createSignal("");

  function initChat() {
    setState("createdAt", Date.now());
  }

  function addUserMessage(content: string, files?: ChatMessageFile[]) {
    setState(
      produce((draft) => {
        const message: ChatMessage = {
          role: "user",
          content,
          timestamp: Date.now(),
        };
        if (files && files.length > 0) {
          message.files = files;
        }
        draft.messages.push(message);
      }),
    );
  }

  function appendAssistantChunk(text: string) {
    setStreamingText((prev) => prev + text);
  }

  function finalizeAssistantMessage() {
    const text = streamingText();
    if (text) {
      setState(
        produce((draft) => {
          draft.messages.push({
            role: "assistant",
            content: text,
            timestamp: Date.now(),
          });
        }),
      );
    }
    setStreamingText("");
  }

  function updatePartialPayload(partial: Partial<AiJobOfferPayload>) {
    setState(
      produce((draft) => {
        draft.partialPayload = { ...draft.partialPayload, ...partial };
      }),
    );
  }

  /**
   * Applies the payload of a `complete` event. A `complete` replaces the
   * category and the subcategories, it does not merge them: otherwise a second
   * `complete` without a category would reopen the review with the first one's
   * (offer-subcategories AC2). The rest of the payload merges as in
   * `updatePartialPayload`.
   */
  function applyCompletePayload(payload: Partial<AiJobOfferPayload>) {
    setState(
      produce((draft) => {
        const next: Partial<AiJobOfferPayload> = {
          ...draft.partialPayload,
          ...payload,
        };
        if (payload.job_category === undefined) delete next.job_category;
        if (payload.job_subcategory === undefined) delete next.job_subcategory;
        draft.partialPayload = next;
      }),
    );
  }

  function reset() {
    setState(reconcile({ messages: [], createdAt: 0, partialPayload: null }));
    setStreamingText("");
    storage?.removeItem(key);
  }

  return {
    get messages() {
      return state.messages;
    },
    get streamingText() {
      return streamingText();
    },
    get partialPayload() {
      return state.partialPayload;
    },
    get createdAt() {
      return state.createdAt;
    },
    initChat,
    addUserMessage,
    appendAssistantChunk,
    finalizeAssistantMessage,
    updatePartialPayload,
    applyCompletePayload,
    reset,
  };
}

export type { AiChatDraft };
export { createAiChatStore };
